import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { Heart, Ribbon, AlertCircle, ExternalLink } from "lucide-react";

const ORGAN_TYPES = [
  { id: "kidney", name: "Kidney", icon: "🫘" },
  { id: "liver", name: "Liver", icon: "🫀" },
  { id: "heart", name: "Heart", icon: "❤️" },
  { id: "lung", name: "Lung(s)", icon: "🫁" },
  { id: "pancreas", name: "Pancreas", icon: "🩺" },
  { id: "intestine", name: "Intestine", icon: "🔄" },
  { id: "cornea", name: "Cornea", icon: "👁️" },
  { id: "bone_marrow", name: "Bone Marrow", icon: "🦴" },
  { id: "stem_cells", name: "Stem Cells", icon: "🧬" },
  { id: "blood", name: "Blood Donation", icon: "🩸" },
  { id: "other", name: "Other", icon: "💜" },
];

const BLOOD_TYPES = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-", "Unknown"];

const URGENCY_LEVELS = [
  { id: "critical", name: "Critical", color: "text-red-600", bg: "bg-red-100" },
  { id: "urgent", name: "Urgent", color: "text-orange-600", bg: "bg-orange-100" },
  { id: "standard", name: "Standard", color: "text-blue-600", bg: "bg-blue-100" },
];

export default function TransplantSettings({ onClose }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [info, setInfo] = useState({
    is_seeking: false,
    organ_needed: "",
    blood_type: "",
    urgency: "standard",
    location: "",
    story: "",
    contact_info: "",
    hospital: "",
    fundraiser_url: "",
    show_on_profile: true,
    show_on_posts: true,
  });

  useEffect(() => {
    fetchInfo();
  }, []);

  const fetchInfo = async () => {
    try {
      const response = await axios.get(`${API}/users/transplant-info`);
      if (response.data.transplant_info) {
        setInfo({ ...info, ...response.data.transplant_info });
      }
    } catch (error) {
      console.error("Error fetching transplant info:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/users/transplant-info`, info);
      toast.success("Transplant information saved! 💜");
      if (onClose) onClose();
    } catch (error) {
      toast.error("Failed to save information");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 pb-4 border-b">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center">
          <span className="text-2xl">💜</span>
        </div>
        <div>
          <h2 className="text-xl font-bold">Organ Transplant Awareness</h2>
          <p className="text-sm text-muted-foreground">
            Help spread awareness if you or a loved one needs a transplant
          </p>
        </div>
      </div>

      {/* Toggle */}
      <Card className={info.is_seeking ? "border-purple-300 bg-purple-50/50" : ""}>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="text-3xl">🎗️</div>
              <div>
                <h3 className="font-semibold">I am seeking an organ transplant</h3>
                <p className="text-sm text-muted-foreground">
                  Enable this to show the awareness ribbon on your profile
                </p>
              </div>
            </div>
            <button
              onClick={() => setInfo({ ...info, is_seeking: !info.is_seeking })}
              className={`w-14 h-8 rounded-full transition-all ${
                info.is_seeking ? "bg-purple-600" : "bg-gray-300"
              }`}
            >
              <div
                className={`w-6 h-6 bg-white rounded-full shadow transition-transform ${
                  info.is_seeking ? "translate-x-7" : "translate-x-1"
                }`}
              />
            </button>
          </div>
        </CardContent>
      </Card>

      {info.is_seeking && (
        <>
          {/* Organ & Blood Type */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Organ/Donation Needed</label>
              <div className="flex flex-wrap gap-2">
                {ORGAN_TYPES.map((organ) => (
                  <button
                    key={organ.id}
                    onClick={() => setInfo({ ...info, organ_needed: organ.id })}
                    className={`px-3 py-2 rounded-lg text-sm transition-all ${
                      info.organ_needed === organ.id
                        ? "bg-purple-600 text-white"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                  >
                    {organ.icon} {organ.name}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Blood Type</label>
              <div className="flex flex-wrap gap-2">
                {BLOOD_TYPES.map((type) => (
                  <button
                    key={type}
                    onClick={() => setInfo({ ...info, blood_type: type })}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      info.blood_type === type
                        ? "bg-red-500 text-white"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Urgency */}
          <div>
            <label className="block text-sm font-medium mb-2">Urgency Level</label>
            <div className="flex gap-3">
              {URGENCY_LEVELS.map((level) => (
                <button
                  key={level.id}
                  onClick={() => setInfo({ ...info, urgency: level.id })}
                  className={`flex-1 py-3 rounded-xl text-sm font-medium transition-all ${
                    info.urgency === level.id
                      ? `${level.bg} ${level.color} ring-2 ring-offset-2 ring-current`
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  {level.id === "critical" && "🚨 "}
                  {level.id === "urgent" && "⚠️ "}
                  {level.id === "standard" && "📋 "}
                  {level.name}
                </button>
              ))}
            </div>
          </div>

          {/* Location & Hospital */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Location</label>
              <Input
                value={info.location}
                onChange={(e) => setInfo({ ...info, location: e.target.value })}
                placeholder="City, State/Country"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Hospital/Medical Center</label>
              <Input
                value={info.hospital}
                onChange={(e) => setInfo({ ...info, hospital: e.target.value })}
                placeholder="Hospital name"
              />
            </div>
          </div>

          {/* Story */}
          <div>
            <label className="block text-sm font-medium mb-2">Your Story (Optional)</label>
            <Textarea
              value={info.story}
              onChange={(e) => setInfo({ ...info, story: e.target.value })}
              placeholder="Share your journey... This helps others connect with your story."
              rows={4}
            />
          </div>

          {/* Contact & Fundraiser */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">How to Help</label>
              <Input
                value={info.contact_info}
                onChange={(e) => setInfo({ ...info, contact_info: e.target.value })}
                placeholder="e.g., Contact the hospital, get tested..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Fundraiser URL (Optional)</label>
              <Input
                value={info.fundraiser_url}
                onChange={(e) => setInfo({ ...info, fundraiser_url: e.target.value })}
                placeholder="https://gofundme.com/..."
              />
            </div>
          </div>

          {/* Visibility Settings */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Visibility Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={info.show_on_profile}
                  onChange={(e) => setInfo({ ...info, show_on_profile: e.target.checked })}
                  className="w-5 h-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <span>Show on my profile page</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={info.show_on_posts}
                  onChange={(e) => setInfo({ ...info, show_on_posts: e.target.checked })}
                  className="w-5 h-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <span>Show ribbon on my posts</span>
              </label>
            </CardContent>
          </Card>

          {/* Info Box */}
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 flex gap-3">
            <AlertCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-purple-800">
              <p className="font-medium mb-1">How this helps</p>
              <p>
                When enabled, a 💜🎗️ ribbon will appear next to your name on posts and profile.
                Others can click it to learn about your situation and how they might help.
                Every view is a chance to find a match and save a life.
              </p>
            </div>
          </div>
        </>
      )}

      {/* Save Button */}
      <div className="flex gap-3 pt-4">
        {onClose && (
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
        )}
        <Button
          onClick={handleSave}
          disabled={saving}
          className="flex-1 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700"
        >
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>
    </div>
  );
}

// Transplant Badge Component - shows on posts/profile
export function TransplantBadge({ info, userName, userPhoto, size = "sm" }) {
  const [showModal, setShowModal] = useState(false);

  if (!info || !info.is_seeking) return null;

  const organ = ORGAN_TYPES.find((o) => o.id === info.organ_needed);
  const urgency = URGENCY_LEVELS.find((u) => u.id === info.urgency);

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-pink-100 to-purple-100 hover:from-pink-200 hover:to-purple-200 transition-all ${
          size === "sm" ? "text-xs" : "text-sm"
        }`}
        title="Click to learn how you can help"
      >
        <span>💜</span>
        <span>🎗️</span>
      </button>

      {/* Modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white rounded-2xl max-w-md w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-pink-500 to-purple-600 p-6 text-white rounded-t-2xl">
              <div className="flex items-center gap-3 mb-3">
                {userPhoto ? (
                  <img
                    src={userPhoto}
                    alt={userName}
                    className="w-14 h-14 rounded-full border-2 border-white"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center text-2xl">
                    {userName?.charAt(0)}
                  </div>
                )}
                <div>
                  <h2 className="text-xl font-bold">{userName}</h2>
                  <p className="text-white/80 text-sm">is seeking a transplant</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-3xl">{organ?.icon || "💜"}</span>
                <span className="text-lg font-medium">{organ?.name || "Organ"} Needed</span>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              {/* Urgency & Blood Type */}
              <div className="flex gap-3">
                {info.urgency && (
                  <div className={`${urgency?.bg} ${urgency?.color} px-3 py-1 rounded-full text-sm font-medium`}>
                    {info.urgency === "critical" && "🚨 "}
                    {info.urgency === "urgent" && "⚠️ "}
                    {urgency?.name}
                  </div>
                )}
                {info.blood_type && (
                  <div className="bg-red-100 text-red-600 px-3 py-1 rounded-full text-sm font-medium">
                    🩸 {info.blood_type}
                  </div>
                )}
              </div>

              {/* Location & Hospital */}
              {(info.location || info.hospital) && (
                <div className="space-y-1 text-sm">
                  {info.location && (
                    <p className="text-gray-600">📍 {info.location}</p>
                  )}
                  {info.hospital && (
                    <p className="text-gray-600">🏥 {info.hospital}</p>
                  )}
                </div>
              )}

              {/* Story */}
              {info.story && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-medium mb-2">Their Story</h3>
                  <p className="text-gray-600 text-sm">{info.story}</p>
                </div>
              )}

              {/* How to Help */}
              {info.contact_info && (
                <div className="bg-purple-50 rounded-xl p-4">
                  <h3 className="font-medium text-purple-800 mb-2">💜 How You Can Help</h3>
                  <p className="text-purple-700 text-sm">{info.contact_info}</p>
                </div>
              )}

              {/* Fundraiser Link */}
              {info.fundraiser_url && (
                <a
                  href={info.fundraiser_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-3 bg-green-500 hover:bg-green-600 text-white rounded-xl font-medium transition-all"
                >
                  <ExternalLink className="w-4 h-4" />
                  Support Their Fundraiser
                </a>
              )}

              {/* Info */}
              <div className="text-center text-sm text-gray-500 pt-2">
                <p>
                  Be the match. Save a life. 💜
                  <br />
                  <a
                    href="https://www.organdonor.gov"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-600 hover:underline"
                  >
                    Learn about organ donation
                  </a>
                </p>
              </div>
            </div>

            {/* Close Button */}
            <div className="p-4 border-t">
              <Button
                onClick={() => setShowModal(false)}
                className="w-full"
                variant="outline"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
